import { z } from 'zod'

/** Operator remote-view / remote-control session TTL (Accessibility path). */
export const PLAYER_REMOTE_SESSION_MAX_TTL_SECONDS = 1_800 as const
export const PLAYER_REMOTE_SESSION_DEFAULT_TTL_SECONDS = 600 as const
export const PLAYER_REMOTE_COMMAND_MAX_TTL_SECONDS = 30 as const
export const PLAYER_REMOTE_COMMAND_DEFAULT_TTL_SECONDS = 15 as const
export const PLAYER_REMOTE_COMMAND_RESULT_TTL_MS = 60_000 as const

/** Operator command route budget, per user per tenant, per 60s window. */
export const PLAYER_REMOTE_COMMAND_OPERATOR_MAX_PER_MINUTE = 120 as const
/**
 * Operator command route budget, per (user, player), per 60s window.
 * Must cover ~1 fps frame capture plus gesture headroom for a full minute.
 */
export const PLAYER_REMOTE_COMMAND_PER_PLAYER_MAX_PER_MINUTE = 90 as const
/** Player result route budget, per player, per 60s window. */
export const PLAYER_REMOTE_RESULT_PLAYER_MAX_PER_MINUTE = 120 as const

/** Player result route budget for the streamed-frame route, per player, per 60s window (1 fps + keepalives + slack). */
export const PLAYER_REMOTE_FRAME_PLAYER_MAX_PER_MINUTE = 90 as const

/** Operator-selectable capture interval while the player streams frames (`startRemoteView.frameIntervalMs`). */
export const PLAYER_REMOTE_FRAME_INTERVAL_MIN_MS = 1_000 as const
export const PLAYER_REMOTE_FRAME_INTERVAL_MAX_MS = 10_000 as const
export const PLAYER_REMOTE_FRAME_INTERVAL_DEFAULT_MS = 1_000 as const
/** While the screen is unchanged the player still sends a tiny "unchanged" beat this often. */
export const PLAYER_REMOTE_FRAME_KEEPALIVE_MS = 10_000 as const

/** Soft cap for JPEG frames returned over the result channel (~720p-ish). */
export const PLAYER_REMOTE_FRAME_JPEG_MAX_BASE64_CHARS = 350_000 as const

export const PLAYER_REMOTE_COMMAND_TYPES = [
  'startRemoteView',
  'startRemoteStream',
  'captureRemoteFrame',
  'stopRemoteView',
  'remoteTap',
  'remoteSwipe'
] as const

export const playerRemoteCommandTypeSchema = z.enum(PLAYER_REMOTE_COMMAND_TYPES)
export type TPlayerRemoteCommandType = z.infer<typeof playerRemoteCommandTypeSchema>

const baseCommand = z.object({
  commandId: z.string().uuid(),
  issuedAt: z.number().int().positive(),
  ttlSeconds: z.number().int().min(1).max(PLAYER_REMOTE_COMMAND_MAX_TTL_SECONDS)
})

const frameIntervalMsSchema = z
  .number()
  .int()
  .min(PLAYER_REMOTE_FRAME_INTERVAL_MIN_MS)
  .max(PLAYER_REMOTE_FRAME_INTERVAL_MAX_MS)

/** Normalized device coords in [0, 1] relative to the current display. */
const normalizedCoord = z.number().min(0).max(1)

export const playerRemoteCommandSchema = z.discriminatedUnion('type', [
  baseCommand
    .extend({
      type: z.literal('startRemoteView'),
      sessionTtlSeconds: z
        .number()
        .int()
        .min(60)
        .max(PLAYER_REMOTE_SESSION_MAX_TTL_SECONDS)
    })
    .strict(),
  /**
   * Ask a player that announced `streamingSupported` to push frames itself at this interval. A separate
   * command (not a field on `startRemoteView`) so players from before streaming — whose strict command
   * schema would drop an unknown key and lose the whole start — never receive it.
   */
  baseCommand
    .extend({ type: z.literal('startRemoteStream'), frameIntervalMs: frameIntervalMsSchema })
    .strict(),
  baseCommand.extend({ type: z.literal('captureRemoteFrame') }).strict(),
  baseCommand.extend({ type: z.literal('stopRemoteView') }).strict(),
  baseCommand
    .extend({
      type: z.literal('remoteTap'),
      x: normalizedCoord,
      y: normalizedCoord
    })
    .strict(),
  baseCommand
    .extend({
      type: z.literal('remoteSwipe'),
      x1: normalizedCoord,
      y1: normalizedCoord,
      x2: normalizedCoord,
      y2: normalizedCoord,
      durationMs: z.number().int().min(50).max(2_000).optional()
    })
    .strict()
])

export type TPlayerRemoteCommand = z.infer<typeof playerRemoteCommandSchema>

export const playerRemoteCommandBodySchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('startRemoteView'),
      sessionTtlSeconds: z
        .number()
        .int()
        .min(60)
        .max(PLAYER_REMOTE_SESSION_MAX_TTL_SECONDS)
        .optional()
    })
    .strict(),
  z.object({ type: z.literal('startRemoteStream'), frameIntervalMs: frameIntervalMsSchema }).strict(),
  z.object({ type: z.literal('captureRemoteFrame') }).strict(),
  z.object({ type: z.literal('stopRemoteView') }).strict(),
  z
    .object({
      type: z.literal('remoteTap'),
      x: normalizedCoord,
      y: normalizedCoord
    })
    .strict(),
  z
    .object({
      type: z.literal('remoteSwipe'),
      x1: normalizedCoord,
      y1: normalizedCoord,
      x2: normalizedCoord,
      y2: normalizedCoord,
      durationMs: z.number().int().min(50).max(2_000).optional()
    })
    .strict()
])

export type TPlayerRemoteCommandBody = z.infer<typeof playerRemoteCommandBodySchema>

export const playerRemoteCommandResultSchema = z
  .object({
    commandId: z.string().uuid(),
    ok: z.boolean(),
    error: z.string().max(512).optional(),
    sessionActiveUntil: z.number().int().positive().optional(),
    accessibilityEnabled: z.boolean().optional(),
    /** JPEG base64 (no data: prefix). Cap enforced by route/player. */
    frameJpegBase64: z.string().max(PLAYER_REMOTE_FRAME_JPEG_MAX_BASE64_CHARS).optional(),
    frameWidth: z.number().int().positive().optional(),
    frameHeight: z.number().int().positive().optional(),
    /** `startRemoteView` result: this player can stream frames, so the app may follow up with `startRemoteStream`. */
    streamingSupported: z.boolean().optional(),
    /** `startRemoteStream` result: the player is now pushing frames (no `captureRemoteFrame` polling needed). */
    streaming: z.boolean().optional()
  })
  .strict()
  .superRefine((r, ctx) => {
    if (r.ok === false && (r.error == null || r.error === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['error'],
        message: 'error is required when ok is false'
      })
    }
  })

export type TPlayerRemoteCommandResult = z.infer<typeof playerRemoteCommandResultSchema>

export interface TPlayerRemoteCommandResponse {
  commandId: string
  delivered: boolean
}

export const playerRemoteRateLimitBodySchema = z
  .object({
    error: z.literal('rate_limited'),
    retryAfterSeconds: z.number().int().positive(),
    scope: z.enum(['user', 'player'])
  })
  .strict()

export type TPlayerRemoteRateLimitBody = z.infer<typeof playerRemoteRateLimitBodySchema>

export function parsePlayerRemoteRateLimitBody(raw: unknown): TPlayerRemoteRateLimitBody | null {
  const parsed = playerRemoteRateLimitBodySchema.safeParse(raw)
  return parsed.success ? parsed.data : null
}

export const playerRemoteResultWsMessageSchema = z
  .object({
    kind: z.literal('player-remote-result'),
    playerId: z.string().min(1),
    result: playerRemoteCommandResultSchema
  })
  .strict()

export type TPlayerRemoteResultWsMessage = z.infer<typeof playerRemoteResultWsMessageSchema>

/**
 * One streamed frame (player → API → operator). Either a full JPEG frame or a tiny `unchanged`
 * keepalive sent while the screen is static, so the operator can tell "static" from "stalled".
 */
export const playerRemoteFrameSchema = z.union([
  z
    .object({
      seq: z.number().int().nonnegative(),
      capturedAt: z.number().int().positive(),
      /** JPEG base64 (no data: prefix). */
      jpegBase64: z.string().min(1).max(PLAYER_REMOTE_FRAME_JPEG_MAX_BASE64_CHARS),
      /** Optional: the native screenshot does not always report the scaled size. */
      width: z.number().int().positive().optional(),
      height: z.number().int().positive().optional()
    })
    .strict(),
  z
    .object({
      seq: z.number().int().nonnegative(),
      capturedAt: z.number().int().positive(),
      unchanged: z.literal(true)
    })
    .strict()
])

export type TPlayerRemoteFrame = z.infer<typeof playerRemoteFrameSchema>

/** True for a full frame (as opposed to an `unchanged` keepalive). */
export function isPlayerRemoteFullFrame(frame: TPlayerRemoteFrame): frame is Extract<TPlayerRemoteFrame, { jpegBase64: string }> {
  return 'jpegBase64' in frame
}

/** API → player reply to a streamed frame; the player stops after repeated zero-viewer replies. */
export const playerRemoteFrameAckSchema = z.object({ viewers: z.number().int().nonnegative() }).strict()
export type TPlayerRemoteFrameAck = z.infer<typeof playerRemoteFrameAckSchema>

export const playerRemoteFrameWsMessageSchema = z
  .object({
    kind: z.literal('player-remote-frame'),
    playerId: z.string().min(1),
    frame: playerRemoteFrameSchema
  })
  .strict()

export type TPlayerRemoteFrameWsMessage = z.infer<typeof playerRemoteFrameWsMessageSchema>
